import type { Nullable } from "./base-types";
import CultureInfo, { InvariantCulture } from "./culture-info";

type Primitive = null | undefined | string | number | boolean | symbol | bigint;

const primitiveTypeNames = ["null", "undefined", "string", "number", "bigint", "boolean", "symbol"] as const;

type PrimitiveTypeName = (typeof primitiveTypeNames)[number];

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function isOfType<T extends Primitive | Function>(type: PrimitiveTypeName | "function") {
    return (value: unknown): value is T => typeof value === type;
}

const isString = isOfType<string>("string");
const isNumberType = isOfType<number>("number");
const isNumber = (value: unknown): value is number => isNumberType(value) && !Number.isNaN(value);
const isInfinite = (value: unknown): value is number =>
    value === Number.POSITIVE_INFINITY || value === Number.NEGATIVE_INFINITY;

export class Guard {
    static notNull(value: unknown, valueName?: string): void {
        if (value === null) throw `${valueName ? valueName : "Value"} should not be null.`;
    }

    static notUndefined(value: unknown, valueName?: string): void {
        if (value === undefined) throw `${valueName ? valueName : "Value"} should not be undefined.`;
    }

    static notNullOrUndefined(value: unknown, valueName?: string): void {
        if (value === null || value === undefined)
            throw Error(
                `${valueName ? valueName : "Value"} should not be null or undefined. Value is ${
                    value === null ? "null" : "undefined"
                }.`
            );
    }

    static parsableInteger(value: any, valueName?: string): void {
        const parsedValue = parseInt(value as string);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        if (isNaN(parsedValue) || parsedValue.toString() !== value.toString()) {
            throw Error(`${valueName ? valueName : `Value '${value}'`} can not be parsed to an integer.`);
        }
    }

    static parsableFloat(value: any, valueName?: string): void {
        const parsedValue = parseFloat(value as string);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        if (isNaN(parsedValue) || parsedValue.toString() !== value.toString()) {
            throw Error(`${valueName ? valueName : `Value '${value}'`} can not be parsed to a float/decimal.`);
        }
    }

    // Values
    static isString(value: Nullable<string>, valueName?: string): void {
        if (isString(value)) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to be a string, got ${value}`);
    }

    static isInteger(value: number, valueName?: string): void {
        if (Number.isInteger(value)) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to be an integer, got ${value}`);
    }

    // Arrays
    static arrayNotEmpty(value: Array<unknown>, valueName?: string): void {
        if (Array.isArray(value) && value.length > 0) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to not be empty`);
    }

    static arrayMaxOne(value: Array<unknown>, valueName?: string): void {
        if (Array.isArray(value) && value.length <= 1) {
            return;
        }

        throw Error(
            `Expected ${valueName ? `'${valueName}'` : ""} to have a maximum length of 1, got \`${value.length}\``
        );
    }

    static arrayNoneNullOrUndefined(value: Array<unknown>, valueName?: string, isOptional = false): void {
        if (!isOptional) {
            Guard.notNullOrUndefined(value);
        } else if (value == null) {
            return;
        }

        if (!Array.isArray(value)) {
            throw Error(`Value ${valueName ? ` '${valueName}' ` : ""}is not an array.`);
        } else if (value.some(item => item == null)) {
            throw Error(`Array ${valueName ? ` '${valueName}' ` : ""}should not contain null or undefined.`);
        }
    }

    static arrayNoneNullOrEmptyString(value: Array<unknown>, valueName?: string, isOptional = false): void {
        if (!isOptional) {
            Guard.notNullOrUndefined(value);
        } else if (value == null) {
            return;
        }

        if (!Array.isArray(value)) {
            throw Error(`Value ${valueName ? ` '${valueName}' ` : ""}is not an array.`);
        } else if (
            value.some(
                item => item == null || (typeof item === "string" && (item.length === 0 || item.trim().length === 0))
            )
        ) {
            throw Error(
                `Array ${valueName ? ` '${valueName}' ` : ""}should not contain null, undefined or empty string.`
            );
        }
    }

    static arrayNoneNullOrInvariantCulture(value: Array<CultureInfo>, valueName?: string, isOptional = false): void {
        if (!isOptional) {
            Guard.notNullOrUndefined(value);
        } else if (value == null) {
            return;
        }

        if (!Array.isArray(value)) {
            throw Error(`Value ${valueName ? ` '${valueName}' ` : ""}is not an array.`);
        } else if (value.some(culture => culture == null || culture === InvariantCulture)) {
            throw Error(
                `Array ${
                    valueName ? ` '${valueName}' ` : ""
                }should not contain null, undefined or '${InvariantCulture}'.`
            );
        }
    }

    static isStringArray(value: Array<unknown>, valueName?: string, isOptional = false): void {
        if (!isOptional) {
            Guard.notNullOrUndefined(value);
        } else if (value == null) {
            return;
        }

        if (!Array.isArray(value)) {
            throw Error(`Value ${valueName ? ` '${valueName}' ` : ""}is not an array.`);
        } else if (value.some(value => typeof value !== "string")) {
            throw Error(`Array ${valueName ? ` '${valueName}' ` : ""}should only contain values of type string.`);
        }
    }

    static isIntegerArray(value: Array<unknown>, valueName?: string, isOptional = false): void {
        if (!isOptional) {
            Guard.notNullOrUndefined(value);
        } else if (value == null) {
            return;
        }

        if (!Array.isArray(value)) {
            throw Error(`Value ${valueName ? ` '${valueName}' ` : ""}is not an array.`);
        } else if (value.some(value => typeof value !== "number" || !Number.isInteger(value))) {
            throw Error(`Array ${valueName ? ` '${valueName}' ` : ""}should only contain values of type number.`);
        }
    }

    static isNumberArray(value: Array<unknown>, valueName?: string, isOptional = false): void {
        if (!isOptional) {
            Guard.notNullOrUndefined(value);
        } else if (value == null) {
            return;
        }

        if (!Array.isArray(value)) {
            throw Error(`Value ${valueName ? ` '${valueName}' ` : ""}is not an array.`);
        } else if (value.some(value => typeof value !== "number")) {
            throw Error(`Array ${valueName ? ` '${valueName}' ` : ""} should only contain values of type number.`);
        }
    }

    // Strings
    static stringNotNullOrEmpty(value: Nullable<string>, valueName?: string): void {
        Guard.notNullOrUndefined(value, valueName);
        Guard.isString(value, valueName);

        if (value?.trim().length === 0) {
            throw Error(
                `Expected ${
                    valueName ? `'${valueName}'` : ""
                } not to be an empty or all whitespace string ('${value}').`
            );
        }
    }

    // Ids
    static validId(value: number, valueName?: string): void {
        Guard.isInteger(value, valueName);

        if (!isInfinite(value) && value > 0) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}' ` : ""} to be a positive integer, got '${value}'.`);
    }

    static validIdOrNull(value: Nullable<number>, valueName?: string): void {
        if (value === null) {
            return;
        }

        Guard.validId(value, valueName);
    }

    static validIds(value: Array<number>, valueName?: string): void {
        if (Array.isArray(value) && value.length === 0) {
            return;
        }

        Guard.isIntegerArray(value, valueName);
        value.some(value => Guard.validId(value, valueName));
    }

    // Cultures
    static notInvariantCulture(culture: CultureInfo, valueName?: string): void {
        Guard.stringNotNullOrEmpty(culture, valueName);

        if (culture !== InvariantCulture) {
            return;
        }

        throw Error(
            `Expected ${valueName ? `'${valueName}'` : ""} to not be equal to '${InvariantCulture}', got '${culture}'.`
        );
    }

    // Numbers
    static notNegative(value: number, valueName?: string): void {
        if (isNumber(value) && value >= 0) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to be positive, got '${value}'.`);
    }

    static greaterThan(number: number, minValue: number, valueName?: string): void {
        if (isNumber(number) && number > minValue) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to be greater than ${minValue}, got '${number}'.`);
    }

    static greaterThanOrEqual(number: number, minValue: number, valueName?: string): void {
        if (isNumber(number) && number >= minValue) {
            return;
        }

        throw Error(
            `Expected ${valueName ? `'${valueName}'` : ""} to be greater than or equal to ${minValue}, got '${number}'.`
        );
    }

    static lessThan(number: number, maxValue: number, valueName?: string): void {
        if (isNumber(number) && number < maxValue) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to be less than ${maxValue}, got '${number}'.`);
    }

    static lessThanOrEqual(number: number, maxValue: number, valueName?: string): void {
        if (isNumber(number) && number <= maxValue) {
            return;
        }

        throw Error(
            `Expected ${valueName ? `'${valueName}'` : ""} to be less than or equal to ${maxValue}, got '${number}'.`
        );
    }

    static isNumber(value: unknown, valueName?: string): void {
        if (isNumber(value)) {
            return;
        }

        throw Error(`Expected ${valueName ? `'${valueName}'` : ""} to be number, got '${value}'.`);
    }
}

export default Guard;
